/**
 * End-to-end payroll setup pipeline — measures real progress across pages
 * (components → templates → template mapping → structure assignment → employees).
 */

import API from "../../../../../services/api";
import { getTemplates, getTemplateComponents, listEmployeeSalaryStructures } from "../../../SalaryApi";
import { v2ListComponents } from "../../SalaryApiV2";
import { buildReadiness, formulaStatus, displayCategory } from "./salaryComponentModel";

function hasStatutoryConfigured(statutoryModule) {
  if (!statutoryModule) return false;
  const { epf, esi, pt, lwf, bonus } = statutoryModule;
  return Boolean(
    epf?.is_enabled ||
      esi?.is_enabled ||
      pt?.is_enabled ||
      lwf?.is_enabled ||
      bonus?.is_enabled
  );
}

export const PAYROLL_SETUP_STEPS = [
  {
    key: "components",
    label: "Payroll Components",
    shortLabel: "Components",
    path: "/salary-v2/components",
    actionLabel: "Create components",
    explainTitle: "Create salary components",
    explainBody:
      "Components are the building blocks of every payslip — Basic, HRA, allowances, deductions, and statutory items. Create at least one active component before moving on.",
  },
  {
    key: "formulas",
    label: "Formula Assignment",
    shortLabel: "Formulas",
    path: "/salary-v2/components",
    hash: "scp-formula-assignment",
    actionLabel: "Assign formulas",
    explainTitle: "Set how components calculate",
    explainBody:
      "Any component using Formula or Statutory (system) calculation needs an expression or system code (e.g. PF, ESI). Fixed-amount components skip this step automatically.",
  },
  {
    key: "statutory",
    label: "Statutory Setup",
    shortLabel: "Statutory",
    path: "/salary-v2/statutory",
    actionLabel: "Configure statutory",
    explainTitle: "Configure PF, ESI, PT & TDS",
    explainBody:
      "Turn on the statutory modules your organisation uses. The payroll engine reads rates and rules from here when computing compliance deductions.",
  },
  {
    key: "templates",
    label: "Salary Templates",
    shortLabel: "Templates",
    path: "/salary-v2/structures",
    actionLabel: "Create template",
    explainTitle: "Create a salary template",
    explainBody:
      "A template is a reusable salary package — e.g. “Standard Staff” or “Management”. You will add components to it in the next step instead of configuring each employee manually.",
  },
  {
    key: "template_components",
    label: "Add Components to Template",
    shortLabel: "Map components",
    path: "/salary-v2/structures",
    actionLabel: "Add to template",
    explainTitle: "Map components into the template",
    explainBody:
      "Open your template and add the salary components it should include. Set fixed amounts, percentages, or link formulas for each line item.",
  },
  {
    key: "assign_structure",
    label: "Assign Package Template",
    shortLabel: "Assign package",
    path: "/employeesalarystructure",
    actionLabel: "Assign structure",
    explainTitle: "Assign template to employees",
    explainBody:
      "Pick an employee, choose the salary template, enter their CTC, and save. Payroll uses this assignment every pay cycle.",
  },
  {
    key: "employees",
    label: "Add Employees",
    shortLabel: "Employees",
    path: "/employees/add",
    actionLabel: "Add employee",
    explainTitle: "Add your workforce",
    explainBody:
      "Create employee records with name, email, joining date, and department. You can assign salary structures right after creating each employee.",
  },
];

function detailForStep(key, snapshot) {
  const componentsConfirmed = Boolean(
    snapshot.componentsConfirmed || snapshot.assignedStructureCount > 0
  );
  switch (key) {
    case "components":
      if (snapshot.componentCount > 0 && componentsConfirmed) {
        return `${snapshot.componentCount} created`;
      }
      if (snapshot.componentCount > 0) return "Defaults ready — confirm in setup";
      return "Not started";
    case "formulas":
      if (!componentsConfirmed) return "Confirm components first";
      if (snapshot.formulaItems === 0) return "No formulas required";
      return snapshot.formulaPending === 0
        ? "All assigned"
        : `${snapshot.formulaPending} pending`;
    case "statutory":
      return snapshot.statutoryConfigured ? "Configured" : "Not configured";
    case "templates":
      if (!componentsConfirmed) return "Confirm components first";
      return snapshot.templateCount > 0 ? `${snapshot.templateCount} template${snapshot.templateCount > 1 ? "s" : ""}` : "None yet";
    case "template_components":
      if (!componentsConfirmed) return "Confirm components first";
      if (snapshot.templateCount === 0) return "Create a template first";
      return snapshot.templatesWithComponents > 0
        ? `${snapshot.templatesWithComponents} template${snapshot.templatesWithComponents > 1 ? "s" : ""} mapped`
        : "Add components to template";
    case "assign_structure":
      return snapshot.assignedStructureCount > 0
        ? `${snapshot.assignedStructureCount} assigned`
        : "Not assigned yet";
    case "employees":
      return snapshot.employeeCount > 0
        ? `${snapshot.employeeCount} employee${snapshot.employeeCount > 1 ? "s" : ""}`
        : "No employees yet";
    default:
      return "—";
  }
}

function isStepComplete(key, snapshot) {
  const componentsConfirmed = Boolean(
    snapshot.componentsConfirmed || snapshot.assignedStructureCount > 0
  );
  const componentsOk = snapshot.componentCount > 0 && componentsConfirmed;
  switch (key) {
    case "components":
      return componentsOk;
    case "formulas":
      return componentsOk && snapshot.formulaPending === 0;
    case "statutory":
      return snapshot.statutoryConfigured;
    case "templates":
      return snapshot.templateCount > 0 && componentsConfirmed;
    case "template_components":
      return snapshot.templatesWithComponents > 0 && componentsConfirmed;
    case "assign_structure":
      return snapshot.assignedStructureCount > 0;
    case "employees":
      return snapshot.employeeCount > 0;
    default:
      return false;
  }
}

function resolveStepPath(step, snapshot) {
  if (step.key === "template_components" && snapshot.firstTemplateNeedingComponents) {
    return `/salary/templates/${snapshot.firstTemplateNeedingComponents}`;
  }
  if (step.key === "templates" && snapshot.templateCount === 0) {
    return step.path;
  }
  if (step.hash) return `${step.path}#${step.hash}`;
  return step.path;
}

export async function fetchPayrollSetupSnapshot() {
  let components = [];
  let templates = [];
  let structures = [];
  let employeeCount = 0;
  let statutoryModule = null;
  let componentsConfirmed = false;

  const [compRes, tplRes, structRes, empRes, statRes, hrRes] = await Promise.allSettled([
    v2ListComponents(),
    getTemplates(),
    listEmployeeSalaryStructures(),
    API.get("/employees/", { params: { limit: 1, active_only: false } }),
    API.get("/statutory/module"),
    API.get("/organisation/me/hr-settings"),
  ]);

  if (compRes.status === "fulfilled") {
    components = Array.isArray(compRes.value) ? compRes.value : [];
  }
  if (tplRes.status === "fulfilled") {
    templates = Array.isArray(tplRes.value) ? tplRes.value : [];
  }
  if (structRes.status === "fulfilled") {
    structures = Array.isArray(structRes.value) ? structRes.value : structRes.value?.items || [];
  }
  if (empRes.status === "fulfilled") {
    const d = empRes.value?.data;
    employeeCount = d?.total ?? d?.count ?? (Array.isArray(d) ? d.length : d?.items?.length ?? 0);
  }
  if (statRes.status === "fulfilled") {
    statutoryModule = statRes.value?.data;
  }
  if (hrRes.status === "fulfilled") {
    const payroll = hrRes.value?.data?.hr_settings?.payroll || {};
    componentsConfirmed = Boolean(payroll.components_completed === true || payroll.components_saved_at);
  }

  const active = components.filter((r) => r.is_active !== false);
  const formulaItems = active.filter((r) => r.calculation_type === "formula" || r.calculation_type === "system");
  const formulaPending = formulaItems.filter((r) => !formulaStatus(r).ready).length;

  let templatesWithComponents = 0;
  let firstTemplateNeedingComponents = null;
  const templateCounts = await Promise.all(
    templates.map(async (t) => {
      try {
        const links = await getTemplateComponents(t.template_id);
        const count = Array.isArray(links) ? links.length : 0;
        return { template_id: t.template_id, count };
      } catch {
        return { template_id: t.template_id, count: 0 };
      }
    })
  );
  for (const row of templateCounts) {
    if (row.count > 0) templatesWithComponents += 1;
    else if (!firstTemplateNeedingComponents) firstTemplateNeedingComponents = row.template_id;
  }
  if (!firstTemplateNeedingComponents && templates[0]?.template_id) {
    firstTemplateNeedingComponents = templates[0].template_id;
  }

  return {
    components,
    componentCount: active.length,
    formulaItems: formulaItems.length,
    formulaPending,
    componentsConfirmed,
    statutoryConfigured: hasStatutoryConfigured(statutoryModule),
    templateCount: templates.length,
    templatesWithComponents,
    firstTemplateNeedingComponents,
    assignedStructureCount: structures.length,
    employeeCount,
  };
}

export function buildPayrollSetupProgress(snapshot) {
  const stages = PAYROLL_SETUP_STEPS.map((step) => {
    const done = isStepComplete(step.key, snapshot);
    return {
      ...step,
      done,
      detail: detailForStep(step.key, snapshot),
      navigateTo: resolveStepPath(step, snapshot),
    };
  });

  const completedCount = stages.filter((s) => s.done).length;
  const totalSteps = stages.length;
  const readiness = totalSteps ? Math.round((completedCount / totalSteps) * 100) : 0;
  const currentStep = stages.find((s) => !s.done) || stages[stages.length - 1];
  const allComplete = completedCount === totalSteps;

  const currentIndex = Math.max(0, stages.findIndex((s) => !s.done));
  let message;
  if (allComplete) {
    message = "Payroll setup is complete — you are ready to run payroll.";
  } else if (currentStep) {
    message = `Step ${currentIndex + 1} of ${totalSteps}: ${currentStep.label} — ${currentStep.detail}`;
  } else {
    message = "Complete the steps below to make payroll ready to run.";
  }

  return {
    stages,
    readiness,
    completedCount,
    totalSteps,
    currentStep,
    allComplete,
    message,
    // Keep local component readiness for formula section on this page
    localReadiness: buildReadiness(snapshot.components || []),
  };
}
